import { ReportTypeCode, type ReportRow } from '@/core/models/reports.models';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { Button } from '@/kit/Button';
import { Checkbox } from '@/kit/Checkbox';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { Icon } from '@/kit/Icon';
import { SelectionBar } from '@/kit/SelectionBar';
import { CannotBeProduced, ReportSource } from './ReportListParts';
import { SendTrialBalance } from './SendTrialBalance';
import { TaxreturnReportsettingsOption } from './TaxreturnReportsettingsOption';
import { useAccountsReportList } from './useAccountsReportList';
import './AccountsReportList.scss';

/** Management Accounts list (Angular TaxreturnManagementaccountsComponent), `/home/taxreturns/reports-managementaccounts`. */
export function TaxreturnManagementaccounts() {
  const list = useAccountsReportList({ reportType: ReportTypeCode.Management, reportTitle: 'Management Accounts' });

  const openReportSettings = () => list.openReportOption(TaxreturnReportsettingsOption);
  const emailReport = (row: ReportRow) => list.email(row, SendTrialBalance);

  const columns: DataTableColumn<ReportRow>[] = [
    {
      id: 'refno',
      header: (
        <>
          <Checkbox
            checked={list.isAllSelected()}
            indeterminate={list.isPartlySelected()}
            onChange={(event) => list.toggleAll(event.target.checked)}
          />
          Ref. No.
        </>
      ),
      cell: (element) => (
        <>
          <Checkbox
            checked={list.selection.has(element.id)}
            onChange={(event) => list.toggleRow(element, event.target.checked)}
          />
          {/* Oct-10 #107 (AP Reports 3507): as in 3.0 and the Annual Accounts list, the reference opens the report preview;
              Edit is in the row menu. */}
          <a className="link" onClick={() => list.previewReport(element)}>
            {element.refNo}
          </a>
          {/* prod-p3: the trial balance behind this report was deleted, so the report can no longer be produced. */}
          <CannotBeProduced row={element} />
        </>
      ),
    },
    {
      id: 'fromdate',
      header: 'From Date',
      headerClassName: 'w-200',
      cell: (element) => <span className="white-space-nowrap">{element.fromDate}</span>,
    },
    {
      id: 'todate',
      header: 'To Date',
      headerClassName: 'w-200',
      cell: (element) => <span className="white-space-nowrap">{element.toDate}</span>,
    },
    {
      // #183: the trial balance(s) the report is built from - the current one, then the comparative one if shown.
      id: 'trialbalances',
      header: 'Trial Balance',
      headerClassName: 'w-200',
      cell: (element) => <span className="white-space-nowrap">{element.trialBalances || '-'}</span>,
    },
    {
      // APT342: Source trial balance or Adjusted trial balance, so a report's figures can be traced.
      id: 'source',
      header: 'Source',
      headerClassName: 'w-200',
      cell: (element) => <ReportSource row={element} />,
    },
    {
      id: 'description',
      header: 'Description',
      cell: (element) => element.description,
    },
    {
      id: 'actions',
      header: 'Action',
      cell: (element) => (
        <div className="d-flex align-items-center grid-actions">
          <Button title="Download" onClick={() => void list.download(element)} disabled={element.canProduce === false}>
            <Icon name="file_download" outlined />
          </Button>
          {/* Oct-10 #106 (AP Reports 3504): Regenerate rebuilds the report from its trial balance after an OK, as 3.0. */}
          <Button
            title="Regenerate"
            onClick={() => void list.regenerate(element)}
            disabled={element.canProduce === false}
          >
            <Icon name="sync" outlined />
          </Button>
          <Button title="Delete" onClick={() => void list.openDeleteClick(element)}>
            <Icon name="delete_outline" outlined />
          </Button>
          <Button title="Edit" onClick={() => list.onEditReport(element)}>
            <Icon name="edit" outlined />
          </Button>
          <Button title="Email" onClick={() => void emailReport(element)}>
            <Icon name="mail_outline" outlined />
          </Button>
          {/* APT342: where the figures came from, the journals in them, and every generation. */}
          <Button title="Source and lineage" onClick={() => list.viewSource(element)}>
            <Icon name="account_tree" outlined />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <SideMenuPage menu="reports" contentClassName="tr-accounts-list">
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <Button className="action-button primary-button" onClick={list.onAddnewReport}>
              <Icon name="add" />
              <span>Add New Report</span>
            </Button>
            {/* #157: no client picker here - this screen is already inside the open client. */}
          </div>

          <div className="content-header-right mb-1">
            <Button className="action-button secondary-button border-button" onClick={openReportSettings}>
              <Icon name="edit_note" outlined />
              Report Settings
            </Button>
          </div>
        </div>
        <SelectionBar
          count={list.selection.size}
          onDelete={() => void list.deleteSelected()}
          onClear={list.clearSelection}
        >
          <Button className="action-button secondary-button" onClick={() => void list.downloadSelected()}>
            <Icon name="file_download" outlined />
            <span>Download selected</span>
          </Button>
        </SelectionBar>
        <div className="mat-table-wrapper">
          <DataTable stickyHeader columns={columns} rows={list.dataSource} rowKey={(row) => row.id} />
        </div>
      </div>
    </SideMenuPage>
  );
}
